import { Box, Grid } from '@mui/material'
import { useIntl } from 'react-intl'
import InfoDisplayItem from '../Typographies/InfoDisplayItem'
import { LabelItem } from '../Typographies/LabelItem'
import { messages } from './messages'

export interface ConsignmentInfoPanelData {
  shipperName?: string
  loadTypeName?: string
  description?: string
  currencyCode?: string
  trackingNumber?: string
  customerReferenceNumber?: string
}

interface ConsignmentInfoPanelProps {
  data: ConsignmentInfoPanelData
  variant?: 'default' | 'sidePanel'
}

const ConsignmentInfoPanel: React.FC<ConsignmentInfoPanelProps> = ({
  data,
  variant = 'default',
}) => {
  const { formatMessage } = useIntl()

  const {
    shipperName,
    loadTypeName,
    description,
    currencyCode,
    trackingNumber,
    customerReferenceNumber,
  } = data

  const isSidePanel = variant === 'sidePanel'

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        border: '1px solid #ccc',
        borderLeft: '4px solid #636363',
        backgroundColor: '#ffffff',
        borderRadius: '4px',
        padding: '16px',
        gap: '16px',
        marginBottom: '16px',
      }}
    >
      <Grid container>
        {/* Row 1: Title | Shipper | Currency | CRN (default) or Filler (sidePanel) */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{ display: 'flex', justifyContent: 'flex-start', mr: '-10px' }}
        >
          <LabelItem>{formatMessage(messages.consignmentInformation)}</LabelItem>
        </Grid>
        <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <InfoDisplayItem
            label={formatMessage(messages.shipper)}
            value={shipperName ?? 'N/A'}
            isSmallScreen={false}
            allowOverlap={true}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
          sx={{ display: 'flex', justifyContent: 'flex-start', pl: '12px' }}
        >
          <InfoDisplayItem
            label={formatMessage(messages.currency)}
            value={currencyCode ?? 'N/A'}
            isSmallScreen={false}
            allowOverlap={true}
          />
        </Grid>
        {!isSidePanel ? (
          <Grid
            item
            xs={12}
            md={3}
            sx={{ display: 'flex', justifyContent: 'flex-start', pl: '20px' }}
          >
            <InfoDisplayItem
              label={formatMessage(messages.customerReferenceNumber)}
              value={customerReferenceNumber ?? 'N/A'}
              isSmallScreen={false}
              allowOverlap={true}
            />
          </Grid>
        ) : (
          <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' } }} />
        )}

        {/* Row 2: Spacer | Type | Tracking Number | CRN (default spans 6) or Filler (sidePanel) */}
        <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' }, mr: '-10px' }} />
        <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <InfoDisplayItem
            label={formatMessage(messages.type)}
            value={loadTypeName ?? 'N/A'}
            isSmallScreen={false}
            allowOverlap={true}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={isSidePanel ? 3 : 6}
          sx={{ display: 'flex', justifyContent: 'flex-start', pl: '12px' }}
        >
          <InfoDisplayItem
            label={formatMessage(messages.trackingNumber)}
            value={trackingNumber ?? 'N/A'}
            isSmallScreen={false}
            allowOverlap={true}
          />
        </Grid>
        {isSidePanel && (
          <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' } }} />
        )}

        {/* Row 3: Spacer | CRN (sidePanel) or nothing | Description */}
        <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' }, mr: '-10px' }} />
        {isSidePanel && (
          <Grid
            item
            xs={12}
            md={3}
            sx={{ display: 'flex', justifyContent: 'flex-start' }}
          >
            <InfoDisplayItem
              label={formatMessage(messages.customerReferenceNumber)}
              value={customerReferenceNumber ?? 'N/A'}
              isSmallScreen={false}
              allowOverlap={true}
            />
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={isSidePanel ? 6 : 9}
          sx={{ display: 'flex', justifyContent: 'flex-start', pl: isSidePanel ? '12px' : undefined }}
        >
          <InfoDisplayItem
            label={formatMessage(messages.description)}
            value={description ?? 'N/A'}
            isSmallScreen={false}
            allowOverlap={true}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default ConsignmentInfoPanel
